import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Consolidated overlay context for modals and drawers.
 *
 * Combines what was previously two separate providers (TherapproveModals
 * and TherapproveDrawers) into a single context so that any component
 * in the tree can open any modal or drawer without nesting-order issues.
 *
 * Consumer hooks:
 *   useTherapproveModals()  — returns the modals slice (backwards-compat)
 *   useTherapproveDrawers() — returns the drawers slice (backwards-compat)
 *   useTherapproveOverlays() — returns the full { modals, drawers } value
 */
import { useDisclosure } from "@mantine/hooks";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
const useOverlayController = () => {
    const [opened, { open: openOverlay, close: closeOverlay }] = useDisclosure(false);
    const [props, setProps] = useState(undefined);
    const open = useCallback((nextProps) => {
        setProps(nextProps);
        openOverlay();
    }, [openOverlay]);
    const close = useCallback(() => {
        closeOverlay();
    }, [closeOverlay]);
    return useMemo(() => ({ opened, props, open, close }), [close, open, opened, props]);
};
// ─── Context ─────────────────────────────────────────────────────────
const TherapproveOverlaysContext = createContext(null);
// ─── State hooks ─────────────────────────────────────────────────────
export const useTherapproveModalsState = () => {
    const editPriorityView = useOverlayController();
    const embeddedCode = useOverlayController();
    const filePreview = useOverlayController();
    const fileUpload = useOverlayController();
    const globalInvite = useOverlayController();
    const helpRequest = useOverlayController();
    const manageProvidersForPatient = useOverlayController();
    const priorityPublicInvite = useOverlayController();
    const priorityReferral = useOverlayController();
    const priorityStatusSimpleEmail = useOverlayController();
    const therapyCategory = useOverlayController();
    const therapySubCategory = useOverlayController();
    return useMemo(() => ({
        editPriorityView, embeddedCode, filePreview, fileUpload,
        globalInvite, helpRequest, manageProvidersForPatient,
        priorityPublicInvite, priorityReferral, priorityStatusSimpleEmail,
        therapyCategory, therapySubCategory
    }), [
        editPriorityView, embeddedCode, filePreview, fileUpload,
        globalInvite, helpRequest, manageProvidersForPatient,
        priorityPublicInvite, priorityReferral, priorityStatusSimpleEmail,
        therapyCategory, therapySubCategory
    ]);
};
export const useTherapproveDrawersState = () => {
    const caregiver = useOverlayController();
    const location = useOverlayController();
    const organization = useOverlayController();
    const patient = useOverlayController();
    const priorityEntry = useOverlayController();
    const settings = useOverlayController();
    const team = useOverlayController();
    return useMemo(() => ({
        caregiver, location, organization, patient,
        priorityEntry, settings, team
    }), [
        caregiver, location, organization, patient,
        priorityEntry, settings, team
    ]);
};
// ─── Provider ────────────────────────────────────────────────────────
export const TherapproveOverlaysContextProvider = ({ children, value }) => {
    return (_jsx(TherapproveOverlaysContext.Provider, { value: value, children: children }));
};
// ─── Consumer hooks ──────────────────────────────────────────────────
/** Returns the full overlays context (modals + drawers). */
export const useTherapproveOverlays = () => {
    const context = useContext(TherapproveOverlaysContext);
    if (!context) {
        throw new Error("useTherapproveOverlays must be used inside a TherapproveOverlayProvider");
    }
    return context;
};
/** Backwards-compatible: returns just the modals slice. */
export const useTherapproveModals = () => {
    return useTherapproveOverlays().modals;
};
/** Backwards-compatible: returns just the drawers slice. */
export const useTherapproveDrawers = () => {
    return useTherapproveOverlays().drawers;
};
